import type { ColorModePlugin, StorefrontTheme, ThemeColors } from '@/features/storefront/themeSchema';
import { themeTokensOverrideSchema } from '@/features/storefront/themeSchema';
import { effectiveTokens } from '@/features/storefront/tokens';
import { contrastRatio, isHexColor, MIN_TEXT_CONTRAST, relativeLuminance } from '@/lib/color';

/**
 * Light and dark mode: a theme in its own colours, or in their opposite.
 *
 * A theme is designed in one mode - nearly always light, Kinetic dark - and
 * the colour mode plugin derives the other from it here. Everything is a pure
 * function of the theme document, so the shop, the theme previews and the
 * tests all agree on what "dark" means for one palette. The result is a theme
 * document like any other: `themeVariables` and every area colour downstream
 * render it without knowing a mode exists.
 */

export type ColorMode = 'light' | 'dark';

/** Below this the page reads as dark: about where #3a3a3a sits. */
const DARK_BACKGROUND_LUMINANCE = 0.18;

/** Where the derived page sits in each mode, as HSL lightness. */
const BACKGROUND_LIGHTNESS: Record<ColorMode, number> = { dark: 0.09, light: 0.98 };
const TEXT_LIGHTNESS: Record<ColorMode, number> = { dark: 0.92, light: 0.12 };

/** The contrast the derived body text is held to: WCAG AAA. */
const TEXT_CONTRAST = 7;

/** Neutrals stay neutral: a cream page must not become a brown one. */
const NEUTRAL_SATURATION = 0.2;

export function oppositeMode(mode: ColorMode): ColorMode {
  return mode === 'dark' ? 'light' : 'dark';
}

/** The mode a palette was designed in, read off its page colour. */
export function paletteMode(colors: Pick<ThemeColors, 'background'>): ColorMode {
  return isHexColor(colors.background) && relativeLuminance(colors.background) < DARK_BACKGROUND_LUMINANCE
    ? 'dark'
    : 'light';
}

type Hsl = [hue: number, saturation: number, lightness: number];

function toHsl(hex: string): Hsl {
  const clean = hex.trim().slice(1);
  const [r, g, b] = [0, 2, 4].map((at) => parseInt(clean.slice(at, at + 2), 16) / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const lightness = (max + min) / 2;
  if (max === min) return [0, 0, lightness];
  const delta = max - min;
  const saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
  const hue =
    max === r ? (g - b) / delta + (g < b ? 6 : 0) : max === g ? (b - r) / delta + 2 : (r - g) / delta + 4;
  return [hue * 60, saturation, lightness];
}

function fromHsl([hue, saturation, lightness]: Hsl): string {
  const s = Math.min(1, Math.max(0, saturation));
  const l = Math.min(1, Math.max(0, lightness));
  const chroma = (1 - Math.abs(2 * l - 1)) * s;
  const x = chroma * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = l - chroma / 2;
  const sector = Math.floor((((hue % 360) + 360) % 360) / 60);
  const [r, g, b] = [
    [chroma, x, 0],
    [x, chroma, 0],
    [0, chroma, x],
    [0, x, chroma],
    [x, 0, chroma],
    [chroma, 0, x],
  ][sector];
  return `#${[r, g, b]
    .map((value) => Math.round((value + m) * 255).toString(16).padStart(2, '0'))
    .join('')}`;
}

/**
 * `color` walked away from `against` in lightness until the two reach
 * `minimum` contrast - lighter on a dark page, darker on a light one. Hue and
 * saturation are kept, so a brand colour stays recognisably itself.
 */
export function withContrast(color: string, against: string, minimum: number): string {
  if (contrastRatio(color, against) >= minimum) return color;
  const [hue, saturation, lightness] = toHsl(color);
  const step = relativeLuminance(against) < DARK_BACKGROUND_LUMINANCE ? 0.02 : -0.02;
  let next = color;
  for (let l = lightness + step; l >= 0 && l <= 1; l += step) {
    next = fromHsl([hue, saturation, l]);
    if (contrastRatio(next, against) >= minimum) return next;
  }
  return step > 0 ? '#ffffff' : '#000000';
}

/**
 * One colour carried into the other mode. Near-white and near-black swap
 * ends, landing on the page colours, so a white header with black type on a
 * light shop is a dark header with light type on a dark one. Mid tones - a
 * green announcement bar, a navy footer - are the merchant's brand rather
 * than their page, and stay as they are.
 */
export function flipHex(hex: string): string {
  const [hue, saturation, lightness] = toHsl(hex);
  if (lightness > 0.2 && lightness < 0.8) return hex.trim();
  const low = BACKGROUND_LIGHTNESS.dark;
  const high = BACKGROUND_LIGHTNESS.light;
  return fromHsl([hue, Math.min(saturation, NEUTRAL_SATURATION), low + (1 - lightness) * (high - low)]);
}

/** The merchant's own picks for the other mode; null is "derive it". */
export type AlternateColors = Pick<
  ColorModePlugin,
  'alternate_primary' | 'alternate_background' | 'alternate_surface' | 'alternate_text' | 'alternate_muted'
>;

/**
 * The palette in `mode`, derived from the theme's own. The page and the text
 * go to that mode's ends, keeping a hint of their hue; the surface keeps its
 * distance from the page; the quiet text keeps its place between them; and
 * the brand colour keeps its hue but is moved until it reads on the new page.
 * Holding primary to text contrast also keeps a solid button legible, since
 * its label is painted in the page colour.
 */
export function derivePalette(colors: ThemeColors, mode: ColorMode, own: Partial<AlternateColors> = {}): ThemeColors {
  const hex = (value: string, fallback: string) => (isHexColor(value) ? value.trim() : fallback);
  const background = hex(colors.background, mode === 'dark' ? '#ffffff' : '#000000');
  const surface = hex(colors.surface, background);
  const text = hex(colors.text, mode === 'dark' ? '#000000' : '#ffffff');
  const muted = hex(colors.muted, text);
  const primary = hex(colors.primary, text);

  const [bgHue, bgSat, bgLight] = toHsl(background);
  const page =
    own.alternate_background ??
    fromHsl([bgHue, Math.min(bgSat, NEUTRAL_SATURATION), BACKGROUND_LIGHTNESS[mode]]);

  const [surfHue, surfSat, surfLight] = toHsl(surface);
  const distance = Math.min(0.12, Math.max(0.04, Math.abs(surfLight - bgLight)));
  const pageLight = toHsl(page)[2];
  const derivedSurface = fromHsl([
    surfHue,
    Math.min(surfSat, NEUTRAL_SATURATION),
    mode === 'dark' ? pageLight + distance : pageLight - distance,
  ]);

  const [textHue, textSat] = toHsl(text);
  const derivedText = withContrast(
    fromHsl([textHue, Math.min(textSat, 0.15), TEXT_LIGHTNESS[mode]]),
    page,
    TEXT_CONTRAST,
  );

  const [mutedHue, mutedSat, mutedLight] = toHsl(muted);
  const derivedMuted = withContrast(
    fromHsl([mutedHue, Math.min(mutedSat, NEUTRAL_SATURATION), 1 - mutedLight]),
    page,
    MIN_TEXT_CONTRAST,
  );

  return {
    primary: own.alternate_primary ?? withContrast(primary, page, MIN_TEXT_CONTRAST),
    background: page,
    surface: own.alternate_surface ?? derivedSurface,
    text: own.alternate_text ?? derivedText,
    muted: own.alternate_muted ?? derivedMuted,
  };
}

/**
 * The colour pairs an area, a section, a block or a part can carry: a
 * background and the text drawn on it.
 */
const COLOR_PAIRS = [
  ['background', 'text_color'],
  ['background_color', 'text_color'],
  ['announcement_background', 'announcement_text_color'],
  ['bottom_bar_background', 'bottom_bar_text_color'],
  ['ribbon_background', 'ribbon_text_color'],
] as const;

/** A field that holds a colour: `background`, `text_color`, `announcement_background`... */
const COLOR_KEY = /(^|_)(color|background)$/;

/**
 * Every hex colour a piece of the theme carries, carried into the other mode -
 * a header, a footer, a section and its blocks, a page part. Fields named for
 * a colour are flipped wherever they sit, however deep; a palette slot
 * (`'surface'`) or a preset name is left alone, since it follows the palette
 * by itself. Returns the very same object when it holds no hex to flip, so a
 * memoised render downstream stays memoised.
 */
export function flipColors<T>(value: T): T {
  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((item) => {
      const flipped = flipColors(item);
      if (flipped !== item) changed = true;
      return flipped;
    });
    return (changed ? next : value) as T;
  }
  if (value === null || typeof value !== 'object') return value;

  const source = value as Record<string, unknown>;
  let next: Record<string, unknown> | null = null;
  for (const [key, field] of Object.entries(source)) {
    const flipped = COLOR_KEY.test(key) && isHexColor(field) ? flipHex(field) : flipColors(field);
    if (flipped !== field) {
      next ??= { ...source };
      next[key] = flipped;
    }
  }
  if (!next) return value;
  // A kept brand background under a flipped text colour can lose its
  // contrast; the text gives way, never the merchant's brand.
  for (const [bg, fg] of COLOR_PAIRS) {
    const background = next[bg];
    const text = next[fg];
    if (isHexColor(background) && isHexColor(text)) {
      next[fg] = withContrast(text, background, MIN_TEXT_CONTRAST);
    }
  }
  return next as T;
}

/**
 * The theme as it renders in `mode`: itself when that is the mode it was
 * designed in, otherwise with the derived palette, its header and footer
 * colours carried across, and the family's accent made to read on the new
 * page. Sections and parts are not flipped here: a merchant's own pages
 * arrive from elsewhere, so `SectionView` and `PartRegion` flip what they
 * draw (see `useColorMode().flipped`).
 */
export function themeInMode(theme: StorefrontTheme, mode: ColorMode, own: Partial<AlternateColors> = {}): StorefrontTheme {
  if (paletteMode(theme.colors) === mode) return theme;
  const colors = derivePalette(theme.colors, mode, own);
  const accent = effectiveTokens(theme).accent;
  const tokens = isHexColor(accent)
    ? themeTokensOverrideSchema.parse({ ...(theme.tokens ?? {}), accent: withContrast(accent, colors.background, 3) })
    : theme.tokens;
  return {
    ...theme,
    colors,
    tokens,
    header: flipColors(theme.header),
    footer: flipColors(theme.footer),
  };
}

/**
 * The mode a shopper sees: their own choice, then the merchant's default -
 * the device's setting, or the theme's own mode.
 */
export function resolveColorMode(
  settings: Pick<ColorModePlugin, 'default_mode'>,
  base: ColorMode,
  saved: ColorMode | null,
  prefersDark: boolean,
): ColorMode {
  if (saved) return saved;
  if (settings.default_mode === 'system') return prefersDark ? 'dark' : 'light';
  return base;
}
